/**
 * How a stored value is shown when it is being read rather than edited.
 *
 * Two rules, and both are product-wide rather than anything one screen decided:
 * an identifier is monospace, and an absent value is a muted dash. They started
 * on the employee record and belong here, because a software licence key and a
 * PAN are the same kind of thing — something read character by character and
 * checked against another screen.
 */

const IDENTIFIER_FIELDS = [
  // people
  "aliasId", "employeeId", "pan", "uan", "aadhaar", "aadhar", "ipNumber",
  "pfNumber", "esiNumber", "esicNumber", "passportNumber", "nationalId", "taxId",
  // money
  "accNumber", "accountNumber", "ifsc", "bankAccount",
  // things
  "assetTag", "serialNumber", "licenseKey", "licenceKey", "imei", "macAddress",
];

const normalise = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const IDENTIFIER_SET = new Set(IDENTIFIER_FIELDS.map(normalise));

export const isIdentifier = (field) => {
  const key = normalise(field);
  if (!key) return false;
  if (IDENTIFIER_SET.has(key)) return true;
  // `panNumber`, `uanNumber`, `aadhaarNo` — the same identifier with a suffix.
  return [...IDENTIFIER_SET].some(
    (id) => id.length >= 3 && (key === `${id}number` || key === `${id}no`)
  );
};

/**
 * An empty value is a dash, not an empty cell: a blank reads as a rendering
 * fault, where a dash says the record has nothing here. `0` and `false` are
 * values and must survive — checking falsiness rather than emptiness would turn
 * a zero balance into "no data".
 */
export const displayValue = (value) => {
  if (value === 0) return "0";
  if (value === false) return "false";
  if (value === null || value === undefined) return "—";
  const s = String(value).trim();
  return s === "" ? "—" : s;
};

export const isEmptyValue = (value) => displayValue(value) === "—";
